import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import { formatDateToDDMMYYYY } from '../../utils';

const stickyCellStyle = {
  background: '#262626',
  fontWeight: 'bold',
  minWidth: 50,
  color: 'white',
  position: 'sticky',
  top: 0,
  zIndex: 100,
};

interface InvoiceBatch {
  id: number;
  generation_date: string;
  generated_by: string | null;
  total_invoices: number;
  successful_emails: number;
  failed_emails: number;
  status: 'in_progress' | 'completed' | 'partial' | 'failed';
  created_at: string;
  agencies: string | null;
}

interface InvoiceDetail {
  id: number;
  client_id: string;
  agency_name: string | null;
  agency_id: number | null;
  invoice_number: string | null;
  invoice_firebase_url: string | null;
  email_sent: boolean;
  email_recipient: string | null;
  email_sent_at: string | null;
  email_error: string | null;
  deal_ids: number[];
  reg_plates: string[];
  total_amount: string;
  cg_count: number;
  da_count: number;
  created_at: string;
}

interface BatchDetails {
  batch: InvoiceBatch;
  details: InvoiceDetail[];
}

const InvoiceBatchesList = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [batches, setBatches] = useState<InvoiceBatch[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Dialog for batch details
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const [selectedBatch, setSelectedBatch] = useState<BatchDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(false);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Retrying email
  const [retryingEmail, setRetryingEmail] = useState<{ [key: number]: boolean }>({});

  const fetchBatches = async () => {
    setIsLoading(true);
    try {
      const response = await makeAuthenticatedApiCall(
        'get',
        `/api/qonto/invoice-batches?limit=${rowsPerPage}&offset=${page * rowsPerPage}`,
      );
      const data = response.data as { batches: InvoiceBatch[]; total: number };
      setBatches(data.batches || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error('Error fetching batches:', error);
      setSnackbarMessage('Erreur lors du chargement des lots de factures');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchBatches();
  }, [page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = async (batchId: number) => {
    setIsLoadingDetails(true);
    setDetailsOpen(true);
    try {
      const response = await makeAuthenticatedApiCall('get', `/api/qonto/invoice-batches/${batchId}`);
      const data = response.data as BatchDetails;
      setSelectedBatch(data);
    } catch (error) {
      console.error('Error fetching batch details:', error);
      setSnackbarMessage('Erreur lors du chargement des détails du lot');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setDetailsOpen(false);
    }
    setIsLoadingDetails(false);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedBatch(null);
  };

  const handleRetryEmail = async (detailId: number, agencyName: string) => {
    setRetryingEmail((prev) => ({ ...prev, [detailId]: true }));
    try {
      const response = await makeAuthenticatedApiCall('post', `/api/qonto/retry-invoice-email/${detailId}`);

      if (response.status === 200) {
        const data = response.data as { message?: string };
        setSnackbarMessage(data.message || `Email renvoyé avec succès pour ${agencyName}`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);

        // Refresh the details
        if (selectedBatch) {
          await handleViewDetails(selectedBatch.batch.id);
        }
      }
    } catch (error: any) {
      console.error('Error retrying email:', error);

      if (error.response) {
        const errorData = error.response.data;
        setSnackbarMessage(errorData.message || `Erreur lors du renvoi de l'email pour ${agencyName}`);
      } else {
        setSnackbarMessage(`Erreur de connexion lors du renvoi de l'email pour ${agencyName}`);
      }
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setRetryingEmail((prev) => ({ ...prev, [detailId]: false }));
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'partial':
        return 'warning';
      case 'failed':
        return 'error';
      case 'in_progress':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Terminé';
      case 'partial':
        return 'Partiel';
      case 'failed':
        return 'Échoué';
      case 'in_progress':
        return 'En cours';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <Grid container direction="column" justifyContent="center" alignItems="center" marginTop={2}>
        <Typography sx={{ marginBottom: 2 }} variant="body1">
          Chargement
        </Typography>
        <CircularProgress size={70} />
      </Grid>
    );
  }

  return (
    <Container maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" gutterBottom sx={{ marginTop: 4 }}>
          Historique des générations de factures
        </Typography>
      </Box>

      <Paper sx={{ marginTop: 3 }} elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={stickyCellStyle}>ID</TableCell>
                <TableCell sx={stickyCellStyle}>Agence</TableCell>
                <TableCell sx={stickyCellStyle}>Date de génération</TableCell>
                <TableCell sx={stickyCellStyle}>Généré par</TableCell>
                <TableCell sx={stickyCellStyle}>Total factures</TableCell>
                <TableCell sx={stickyCellStyle}>Emails réussis</TableCell>
                <TableCell sx={stickyCellStyle}>Emails échoués</TableCell>
                <TableCell sx={stickyCellStyle}>Statut</TableCell>
                <TableCell sx={stickyCellStyle}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Aucun lot de génération trouvé
                  </TableCell>
                </TableRow>
              ) : (
                batches.map((batch) => (
                  <TableRow key={batch.id}>
                    <TableCell>{batch.id}</TableCell>
                    <TableCell>{batch.agencies || 'N/A'}</TableCell>
                    <TableCell>{formatDateToDDMMYYYY(batch.generation_date)}</TableCell>
                    <TableCell>{batch.generated_by || 'Automatique'}</TableCell>
                    <TableCell>{batch.total_invoices}</TableCell>
                    <TableCell>{batch.successful_emails}</TableCell>
                    <TableCell>{batch.failed_emails}</TableCell>
                    <TableCell>
                      <Chip label={getStatusLabel(batch.status)} color={getStatusColor(batch.status)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          fontWeight: 600,
                          color: 'black !important',
                          borderColor: '#fdbb5f',
                        }}
                        onClick={() => handleViewDetails(batch.id)}
                      >
                        Voir détails
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="lg" fullWidth>
        <DialogTitle>
          {selectedBatch && (
            <Box>
              <Typography variant="h5">Détails du lot #{selectedBatch.batch.id}</Typography>
              <Typography variant="body2" color="text.secondary">
                Généré le {formatDateToDDMMYYYY(selectedBatch.batch.generation_date)} -{' '}
                {selectedBatch.batch.total_invoices} facture(s) - {selectedBatch.batch.successful_emails} email(s)
                envoyé(s) avec succès
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {isLoadingDetails ? (
            <Grid container direction="column" justifyContent="center" alignItems="center" sx={{ padding: 4 }}>
              <CircularProgress size={50} />
            </Grid>
          ) : selectedBatch ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Client ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Agence</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>N° Facture</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Immatriculations</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Montant</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>CG/DA</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Statut Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedBatch.details.map((detail) => (
                    <TableRow key={detail.id}>
                      <TableCell>{detail.client_id}</TableCell>
                      <TableCell>{detail.agency_name || 'N/A'}</TableCell>
                      <TableCell>
                        {detail.invoice_number ? (
                          <a
                            href={detail.invoice_firebase_url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#EF8F35', fontWeight: 600, textDecoration: 'none' }}
                          >
                            {detail.invoice_number}
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 200 }}>
                          {detail.reg_plates && detail.reg_plates.length > 0 ? detail.reg_plates.join(', ') : 'N/A'}
                        </Box>
                      </TableCell>
                      <TableCell>{parseFloat(detail.total_amount).toFixed(2)} €</TableCell>
                      <TableCell>
                        {detail.cg_count > 0 && `${detail.cg_count} CG`}
                        {detail.cg_count > 0 && detail.da_count > 0 && ', '}
                        {detail.da_count > 0 && `${detail.da_count} DA`}
                      </TableCell>
                      <TableCell>{detail.email_recipient || 'N/A'}</TableCell>
                      <TableCell>
                        {detail.email_sent ? (
                          <Chip label="Envoyé" color="success" size="small" />
                        ) : (
                          <Box>
                            <Chip label="Échec" color="error" size="small" />
                            {detail.email_error && (
                              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                {detail.email_error}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {!detail.email_sent && detail.email_recipient && (
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              fontWeight: 600,
                              color: 'black !important',
                              borderColor: '#fdbb5f',
                            }}
                            onClick={() => handleRetryEmail(detail.id, detail.agency_name || 'ce client')}
                            disabled={retryingEmail[detail.id] || false}
                          >
                            {retryingEmail[detail.id] ? <CircularProgress size={16} /> : 'Renvoyer'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDetails}
            sx={{
              fontWeight: 600,
              color: 'black !important',
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default InvoiceBatchesList;
